import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiSearch, FiTag, FiDownload, FiFileText, FiChevronsUp, FiChevronsDown,
  FiChevronLeft, FiChevronRight, FiCalendar, FiPieChart, FiTrendingUp,
} from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --er-teal:      #0a5556;
  --er-teal-mid:  #0c7172;
  --er-teal-lite: #14b8a6;
  --er-gold:      #FFD166;
  --er-bg:        #eef6f6;
  --er-white:     #ffffff;
  --er-border:    rgba(10,85,86,0.10);
  --er-text:      #0f2a2a;
  --er-muted:     #5a8080;
  --er-danger:    #ef4444;
  --er-success:   #10b981;
}

@keyframes er__fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
@keyframes er__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes er__spin    { to{transform:rotate(360deg)} }

.er__wrap {
  font-family:'DM Sans',sans-serif;
  padding:26px 26px 60px;
  background:var(--er-bg);
  min-height:100vh;
  animation:er__fadeUp 0.4s ease both;
}

/* ── header ── */
.er__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
.er__hd-left { display:flex; align-items:center; gap:14px; }
.er__hd-icon {
  width:48px; height:48px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,var(--er-teal),var(--er-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:20px; color:#fff; box-shadow:0 6px 18px rgba(10,85,86,0.26);
}
.er__hd-title { font-family:'Sora',sans-serif; font-size:20px; font-weight:800; color:var(--er-text); letter-spacing:-0.03em; margin:0 0 3px; }
.er__hd-sub   { font-size:12.5px; color:var(--er-muted); margin:0; }

.er__download-btn {
  display:inline-flex; align-items:center; gap:8px;
  padding:10px 20px; border-radius:12px; border:none; cursor:pointer;
  font-size:13.5px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--er-teal),var(--er-teal-lite));
  box-shadow:0 4px 16px rgba(10,85,86,0.28);
  transition:transform 0.14s, box-shadow 0.18s;
}
.er__download-btn:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(10,85,86,0.34); }

/* ── stats ── */
.er__stats { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
.er__stat {
  flex:1; min-width:160px;
  background:var(--er-white); border:1px solid var(--er-border); border-radius:16px;
  padding:16px 18px; box-shadow:0 2px 10px rgba(10,85,86,0.05);
  display:flex; align-items:center; gap:14px; position:relative; overflow:hidden;
}
.er__stat::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--er-teal),var(--er-teal-lite),var(--er-gold));
}
.er__stat-icon {
  width:44px; height:44px; border-radius:13px; flex-shrink:0;
  background:linear-gradient(135deg,var(--er-teal),var(--er-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:19px; color:#fff; box-shadow:0 4px 12px rgba(10,85,86,0.22);
}
.er__stat-lbl { font-size:11.5px; color:var(--er-muted); font-weight:500; margin-bottom:3px; }
.er__stat-val { font-family:'Sora',sans-serif; font-size:17px; font-weight:800; color:var(--er-text); }

/* ── main card ── */
.er__card {
  background:var(--er-white); border-radius:20px;
  border:1px solid var(--er-border);
  box-shadow:0 2px 20px rgba(10,85,86,0.07),0 8px 40px rgba(0,0,0,0.03);
  overflow:hidden; position:relative;
}
.er__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--er-teal) 0%,var(--er-teal-lite) 55%,var(--er-gold) 100%);
}

/* ── toolbar ── */
.er__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:14px; padding:18px 22px; flex-wrap:wrap;
  border-bottom:1px solid var(--er-border);
}
.er__toolbar-left { display:flex; align-items:center; gap:10px; }
.er__show-lbl { font-size:12.5px; color:var(--er-muted); white-space:nowrap; }
.er__select {
  padding:7px 28px 7px 11px; font-size:12.5px;
  color:var(--er-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.15); border-radius:9px;
  outline:none; cursor:pointer; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.er__select:focus { border-color:var(--er-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); }
.er__search {
  display:flex; align-items:center; gap:8px;
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.15);
  border-radius:10px; padding:8px 13px;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.er__search:focus-within { border-color:var(--er-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.er__search-icon  { color:var(--er-muted); font-size:14px; flex-shrink:0; }
.er__search-input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--er-text); width:220px;
}
.er__search-input::placeholder { color:#9ababa; }

/* ── table ── */
.er__table-wrap { overflow-x:auto; }
.er__table { width:100%; border-collapse:collapse; }
.er__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--er-teal);
  background:rgba(10,85,86,0.04); padding:11px 18px;
  white-space:nowrap; border-bottom:1px solid var(--er-border);
  cursor:pointer; user-select:none;
}
.er__table thead th:hover { color:var(--er-teal-lite); }
.er__sort-icon { display:inline-flex; vertical-align:middle; margin-left:3px; opacity:.45; }
.er__sort-icon--on { opacity:1; color:var(--er-teal-lite); }

.er__table tbody tr { border-bottom:1px solid rgba(10,85,86,0.055); transition:background 0.13s; }
.er__table tbody tr:last-child { border-bottom:none; }
.er__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.er__table tbody td { padding:12px 18px; font-size:13.5px; color:var(--er-text); vertical-align:middle; }

/* cells */
.er__date-cell { display:flex; align-items:center; gap:6px; font-size:13px; color:var(--er-muted); }
.er__desc-title { font-size:13px; font-weight:600; color:var(--er-text); }
.er__desc-sub   { font-size:11.5px; color:var(--er-muted); margin-top:1px; }
.er__cat-badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
  background:rgba(10,85,86,0.07); color:var(--er-teal);
  border:1px solid rgba(10,85,86,0.14); border-radius:50px;
  padding:3px 10px; white-space:nowrap;
}
.er__tax-val    { font-size:13px; color:var(--er-muted); text-align:right; }
.er__amount-val { font-family:'Sora',sans-serif; font-size:14px; font-weight:800; color:var(--er-teal); text-align:right; }

/* shimmer */
.er__skel {
  border-radius:8px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:er__shimmer 1.4s linear infinite;
}
.er__skel-stat { height:14px; border-radius:6px; }

/* empty */
.er__empty { padding:60px 20px; text-align:center; }
.er__empty-icon { width:56px; height:56px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:24px; }
.er__empty-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:700; color:var(--er-text); margin-bottom:5px; }
.er__empty-sub   { font-size:13px; color:var(--er-muted); }

/* footer */
.er__footer {
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 22px; border-top:1px solid var(--er-border); flex-wrap:wrap; gap:10px;
}
.er__footer-left { display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
.er__footer-info { font-size:12.5px; color:var(--er-muted); }
.er__footer-info strong { color:var(--er-text); }
.er__footer-totals { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
.er__footer-total {
  display:inline-flex; align-items:center; gap:6px;
  font-size:12px; font-weight:700; font-family:'Sora',sans-serif;
  background:rgba(10,85,86,0.07); color:var(--er-teal);
  border:1px solid rgba(10,85,86,0.14); border-radius:9px; padding:4px 12px;
}

/* pagination */
.er__pagination { display:flex; align-items:center; gap:4px; }
.er__page-btn {
  min-width:32px; height:32px; border-radius:9px; border:1.5px solid var(--er-border);
  background:var(--er-white); font-size:12px; font-weight:600; color:var(--er-muted);
  cursor:pointer; padding:0 8px; display:flex; align-items:center; justify-content:center; gap:3px;
  transition:all 0.15s;
}
.er__page-btn:hover:not(:disabled) { border-color:var(--er-teal); color:var(--er-teal); background:rgba(10,85,86,0.05); }
.er__page-btn--active { background:var(--er-teal) !important; border-color:var(--er-teal) !important; color:#fff !important; }
.er__page-btn:disabled { opacity:.35; cursor:not-allowed; }

@media(max-width:700px){ .er__search-input{width:140px;} .er__footer-totals{display:none;} }
`;

/* ─── Helpers ─── */
const formatCurrency = (amount) =>
  "₹" + Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2 });

function CustomPagination({ totalItems, itemsPerPage, currentPage, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <div className="er__pagination">
      <button className="er__page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        <FiChevronLeft size={12} />
      </button>
      {pages.map(p => (
        <button key={p} className={`er__page-btn ${currentPage === p ? "er__page-btn--active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      ))}
      <button className="er__page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        <FiChevronRight size={12} />
      </button>
    </div>
  );
}

/* ─── Component ─── */
function ExpenseReport() {
  const [expenses, setExpenses]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy]         = useState({ key:"expense_date", direction:"desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const token = localStorage.getItem("auth_token");

  /* ── fetch (unchanged) ── */
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getAllExpenses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) setExpenses(response.data.data);
    } catch (error) {
      Swal.fire("Error", "Failed to fetch report data", "error");
    } finally {
      setTimeout(() => setLoading(false), 600);
    }
  };

  useEffect(() => { fetchExpenses(); }, []);

  /* ── process (unchanged) ── */
  const processedExpenses = useMemo(() => {
    let list = [...expenses];
    if (searchTerm.trim()) {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(e =>
        e.title?.toLowerCase().includes(s) ||
        e.vendor?.toLowerCase().includes(s) ||
        e.category?.toLowerCase().includes(s)
      );
    }
    list.sort((a, b) => {
      const aVal = a[sortBy.key] || "", bVal = b[sortBy.key] || "";
      let cmp = !isNaN(aVal) && !isNaN(bVal) ? Number(aVal) - Number(bVal) : String(aVal).localeCompare(String(bVal));
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [expenses, searchTerm, sortBy]);

  /* ── summary (unchanged) ── */
  const summary = useMemo(() => {
    const total = processedExpenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const tax   = processedExpenses.reduce((acc, curr) => acc + Number(curr.tax_amount || 0), 0);
    return { total, tax, count: processedExpenses.length };
  }, [processedExpenses]);

  const totalItems = processedExpenses.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated  = processedExpenses.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => setSortBy(prev => ({
    key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
  }));

  const SortIcon = ({ columnKey }) => {
    const isActive = sortBy.key === columnKey;
    const cls = `er__sort-icon${isActive ? " er__sort-icon--on" : ""}`;
    return isActive && sortBy.direction === "asc"
      ? <FiChevronsUp size={13} className={cls} />
      : <FiChevronsDown size={13} className={cls} />;
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="er__wrap">

          {/* ── Header ── */}
          <div className="er__hd">
            <div className="er__hd-left">
              <div className="er__hd-icon"><FiFileText /></div>
              <div>
                <div className="er__hd-title">Expense Analysis Report</div>
                <div className="er__hd-sub">Comprehensive breakdown of business expenditures and tax components</div>
              </div>
            </div>
            <button className="er__download-btn" onClick={() => window.print()}>
              <FiDownload size={15} /> Download PDF
            </button>
          </div>

          {/* ── Stats ── */}
          <div className="er__stats">
            {[
              { label:"Total Expenditure",   value: formatCurrency(summary.total), icon:<FiTrendingUp size={20} /> },
              { label:"Tax Component",        value: formatCurrency(summary.tax),   icon:<FiPieChart size={20} /> },
              { label:"Total Transactions",   value: summary.count,                 icon:<FiFileText size={20} /> },
            ].map((kpi, idx) => (
              <div className="er__stat" key={idx}>
                <div className="er__stat-icon">{kpi.icon}</div>
                <div>
                  <div className="er__stat-lbl">{kpi.label}</div>
                  <div className="er__stat-val">
                    {loading
                      ? <div className="er__skel er__skel-stat" style={{ width:80, marginTop:4 }} />
                      : kpi.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Main Card ── */}
          <div className="er__card">

            {/* Toolbar */}
            <div className="er__toolbar">
              <div className="er__toolbar-left">
                <span className="er__show-lbl">Show</span>
                <select className="er__select" value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="er__search">
                <FiSearch className="er__search-icon" />
                <input className="er__search-input"
                  placeholder="Filter by title, vendor, or category…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
              </div>
            </div>

            {/* Table */}
            <div className="er__table-wrap">
              <table className="er__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("expense_date")}>Date <SortIcon columnKey="expense_date" /></th>
                    <th onClick={() => handleSort("title")}>Description <SortIcon columnKey="title" /></th>
                    <th onClick={() => handleSort("category")}>Category <SortIcon columnKey="category" /></th>
                    <th style={{ textAlign:"right" }} onClick={() => handleSort("tax_amount")}>Tax <SortIcon columnKey="tax_amount" /></th>
                    <th style={{ textAlign:"right" }} onClick={() => handleSort("amount")}>Total Amount <SortIcon columnKey="amount" /></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td><div className="er__skel" style={{ height:14, width:90 }} /></td>
                        <td>
                          <div className="er__skel" style={{ height:14, width:"70%", marginBottom:6 }} />
                          <div className="er__skel" style={{ height:11, width:"40%" }} />
                        </td>
                        <td><div className="er__skel" style={{ height:22, width:80, borderRadius:50 }} /></td>
                        <td><div className="er__skel" style={{ height:14, width:60, marginLeft:"auto" }} /></td>
                        <td><div className="er__skel" style={{ height:14, width:80, marginLeft:"auto" }} /></td>
                      </tr>
                    ))
                  ) : paginated.length > 0 ? (
                    paginated.map((exp) => (
                      <tr key={exp.expense_uuid}>
                        <td>
                          <div className="er__date-cell">
                            <FiCalendar size={12} />
                            {new Date(exp.expense_date).toLocaleDateString("en-GB")}
                          </div>
                        </td>
                        <td>
                          <div className="er__desc-title">{exp.title}</div>
                          <div className="er__desc-sub">{exp.vendor} {exp.vendor && "•"} {exp.payment_mode}</div>
                        </td>
                        <td>
                          <span className="er__cat-badge"><FiTag size={10} />{exp.category}</span>
                        </td>
                        <td>
                          <div className="er__tax-val">{formatCurrency(exp.tax_amount || 0)}</div>
                        </td>
                        <td>
                          <div className="er__amount-val">{formatCurrency(exp.amount)}</div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5">
                      <div className="er__empty">
                        <div className="er__empty-icon">🔍</div>
                        <div className="er__empty-title">No report data found</div>
                        <div className="er__empty-sub">No expense records match your current filters.</div>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="er__footer">
              <div className="er__footer-left">
                <div className="er__footer-info">
                  Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to{" "}
                  <strong>{Math.min(startIndex + itemsPerPage, totalItems)}</strong> of{" "}
                  <strong>{totalItems}</strong> entries
                </div>
                {!loading && (
                  <div className="er__footer-totals">
                    <span className="er__footer-total"><FiTrendingUp size={11} /> {formatCurrency(summary.total)}</span>
                    <span className="er__footer-total"><FiPieChart size={11} /> Tax: {formatCurrency(summary.tax)}</span>
                  </div>
                )}
              </div>
              <CustomPagination
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>

          </div>
        </div>
      </main>
    </>
  );
}

export default ExpenseReport;
